import { FormikErrors, getIn, setIn } from 'formik';
import { useCallback } from 'react';

import { ValidationResponse } from '../../../../types/ValidationResponse';
import { validateOrderCreation, validateOrderUpdate } from '../../../common/api/clients/order.api';
import { queries } from '../../../common/api/queryKeys/queries';
import { mapValidationResponseToFormikErrors } from '../../../common/validation/mapValidationResponseToFormikErrors';
import { useBackendValidationRequest } from '../../../common/validation/useBackendValidationRequest';
import { FormikCreateEnquiryValues } from '../../create-enquiry/CreateEnquiryFlow';
import { buildOrderRequest } from '../../create-enquiry/util/buildOrderRequest';
import { OrderState } from '../../model/OrderModel';

interface ValidateOrderRequest {
    orderId?: string;
    validationRequest: ReturnType<typeof buildOrderRequest>;
}

export const useValidateCreateEnquiryForm = (orderId?: string) => {
    const requestValidation = useBackendValidationRequest<ValidateOrderRequest, ValidationResponse>({
        queryKeyPrefix: queries.backendValidation.orderCreateEnquiry.queryKey,
        request: ({ orderId, validationRequest }) => {
            return orderId ? validateOrderUpdate(validationRequest, orderId) : validateOrderCreation(validationRequest);
        },
    });

    return useCallback(
        async (values: FormikCreateEnquiryValues): Promise<FormikErrors<FormikCreateEnquiryValues>> => {
            const validationRequest = {
                ...buildOrderRequest(values, OrderState.OPEN),
                receiverSupplierIds: values.receiverSupplierIds || [],
            };
            const validationKey = JSON.stringify({
                orderId: orderId || null,
                validationRequest,
            });

            try {
                const response = await requestValidation({
                    validationKey,
                    params: {
                        orderId,
                        validationRequest,
                    },
                });

                const errors = mapValidationResponseToFormikErrors<FormikCreateEnquiryValues>(response);
                const itemsError = getIn(errors, 'items');
                const firstActiveItemIndex = values.items.findIndex((item) => !item.deleted);

                if (typeof itemsError === 'string' && firstActiveItemIndex >= 0) {
                    return setIn(errors, `items[${firstActiveItemIndex}].productId`, true);
                }

                return errors;
            } catch {
                return {};
            }
        },
        [orderId, requestValidation]
    );
};
